import React from 'react';
import { Menu, Bell, Search, User, Settings, LogOut, CreditCard, BarChart3, UserPlus, Share, AlertTriangle, Users, GitBranch, Activity, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearch } from '../../contexts/SearchContext';
import { motion, AnimatePresence } from 'framer-motion';
import { AvatarService } from '../../lib/avatar-service';
import { useNavigate, useLocation } from 'react-router-dom';
import { SearchResults } from '../search/SearchResults';
import { MobileSearchModal } from '../search/MobileSearchModal';
import { NotificationService, type NotificationWithIcon } from '../../lib/notifications';
import { UnifiedCreditService, type UnifiedCreditData } from '../../lib/unified-credit-service';
import { DualCreditDisplay } from '../credits/DualCreditDisplay';
import { subscribeToCreditChanges } from '../../lib/credit-events';

interface NavbarProps {
  onToggleSidebar: () => void;
}

export function Navbar({ onToggleSidebar }: NavbarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    query,
    setQuery,
    openSearch,
    closeSearch,
    openMobileSearch,
    closeMobileSearch,
    isMobileSearchOpen,
    isMobile
  } = useSearch();
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [notifications, setNotifications] = React.useState<NotificationWithIcon[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [loadingNotifications, setLoadingNotifications] = React.useState(false);
  const [creditData, setCreditData] = React.useState<UnifiedCreditData | null>(null);
  const notificationRef = React.useRef<HTMLDivElement>(null);
  const userMenuRef = React.useRef<HTMLDivElement>(null);

  const handleProfileClick = () => {
    navigate('/dashboard/settings?tab=profile');
    setShowUserMenu(false);
  };

  const handleSettingsClick = () => {
    navigate('/dashboard/settings');
    setShowUserMenu(false);
  };

  const handleSearchClick = () => {
    if (isMobile) {
      openMobileSearch();
    } else {
      if (query.trim()) {
        navigate(`/dashboard/search?q=${encodeURIComponent(query.trim())}`);
      } else {
        navigate('/dashboard/search');
      }
      closeSearch();
    }
  };

  const handleSearchFocus = () => {
    if (!isMobile) {
      openSearch();
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearchClick();
  };

  // Load notifications
  const loadNotifications = React.useCallback(async () => {
    if (!user) return;
    
    setLoadingNotifications(true);
    try {
      const [notificationsData, unreadCountData] = await Promise.all([
        NotificationService.getUserNotifications(10),
        NotificationService.getUnreadCount()
      ]);
      
      setNotifications(notificationsData);
      setUnreadCount(unreadCountData);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  }, [user]);

  // Load notifications on mount and when user changes
  React.useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Load credit data
  React.useEffect(() => {
    const loadCreditData = async () => {
      if (!user?.id) return;
      try {
        const data = await UnifiedCreditService.getCreditData(user.id);
        setCreditData(data);
      } catch (error) {
        console.error('Failed to load credit data:', error);
      }
    };

    loadCreditData();
    // Refresh credits every 30 seconds
    const interval = setInterval(loadCreditData, 30000);
    
    return () => clearInterval(interval);
  }, [user]);

  // Listen for real-time credit changes
  React.useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = subscribeToCreditChanges(user.id, (event) => {
      console.log('💳 Credit change event received in Navbar:', event);
      // Refresh credit data when credits change
      UnifiedCreditService.getCreditData(user.id).then(setCreditData).catch(console.error);
    });

    return unsubscribe;
  }, [user]);

  // Refresh credits when navigating to analysis pages (immediate update after analysis)
  React.useEffect(() => {
    if (!user?.id) return;
    
    // Check if we navigated to an analysis page
    if (location.pathname.includes('/dashboard/analysis')) {
      UnifiedCreditService.getCreditData(user.id).then(setCreditData).catch(console.error);
    }
  }, [location.pathname, user?.id]);

  // Set up real-time notifications
  React.useEffect(() => {
    if (!user) return;

    const unsubscribe = NotificationService.subscribeToNotifications((newNotification) => {
      // Only show notifications for the current user
      if (newNotification.user_id === user.id) {
        setNotifications(prev => [newNotification, ...prev.slice(0, 9)]);
        setUnreadCount(prev => prev + 1);
      }
    });

    return unsubscribe || (() => {});
  }, [user]);

  // Close notification dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  // Close user menu dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleNotificationClick = async (notification: NotificationWithIcon) => {
    if (!notification.read) {
      await NotificationService.markAsRead(notification.id);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => 
        prev.map(n => 
          n.id === notification.id 
            ? { ...n, read: true, read_at: new Date().toISOString() }
            : n
        )
      );
    }

    if (notification.action_url) {
      navigate(notification.action_url);
    }
    setShowNotifications(false);
  };

  const handleMarkAllAsRead = async () => {
    const success = await NotificationService.markAllAsRead();
    if (success) {
      setUnreadCount(0);
      setNotifications(prev => 
        prev.map(n => ({ 
          ...n, 
          read: true, 
          read_at: new Date().toISOString() 
        }))
      );
    }
  };

  const getNotificationIcon = (iconName: string) => {
    const iconMap: Record<string, any> = {
      'BarChart3': BarChart3,
      'UserPlus': UserPlus,
      'Share': Share,
      'AlertTriangle': AlertTriangle,
      'CreditCard': CreditCard,
      'Users': Users,
      'GitBranch': GitBranch,
      'Bell': Bell,
      'Activity': Activity
    };
    
    const IconComponent = iconMap[iconName] || Bell;
    return IconComponent;
  };

  return (
    <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sticky top-0 z-10">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <button 
            onClick={onToggleSidebar}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          
          {/* Mobile Search Button */}
          {isMobile ? (
            <button
              onClick={handleSearchClick}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <Search className="w-5 h-5 text-gray-600" />
            </button>
          ) : (
            /* Desktop Search Bar */
            <div className="relative flex-1 max-w-sm md:max-w-md lg:max-w-lg" data-search-container>
              <form onSubmit={handleSearchSubmit}>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={handleSearchFocus}
                  placeholder="Search creatives..."
                  className="pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full"
                />
              </form>
              <SearchResults 
                isDropdown={true}
                maxResults={8}
                showLoadMore={false}
                onClose={closeSearch}
              />
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2 sm:space-x-4">
          {/* Credits Display - hide on very small screens */}
          {creditData && creditData.adCreative && (
            <div className="hidden sm:block">
              <DualCreditDisplay
                analysisCredits={creditData.availableCredits}
                adCreativeCredits={creditData.adCreative.availableCredits}
                size="small"
                orientation="horizontal"
                className="mr-2"
              />
            </div>
          )}

          {/* Contact Us */}
          <div className="relative">
            <button 
              onClick={() => navigate('/dashboard/contact')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center group"
              title="Contact Support"
            >
              <MessageSquare className="w-5 h-5 text-gray-600 group-hover:text-purple-600 transition-colors" />
            </button>
          </div>

          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <Bell className="w-5 h-5 text-gray-600" />
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
              )}
            </button>
            
            <AnimatePresence>
              {showNotifications && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                >
                  <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllAsRead}
                        className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {loadingNotifications ? (
                      <div className="p-4 text-center text-gray-500">
                        <div className="animate-spin w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
                        <p className="mt-2 text-sm">Loading notifications...</p>
                      </div>
                    ) : notifications.length > 0 ? (
                      <div className="space-y-0">
                        {notifications.map((notification) => {
                          const IconComponent = getNotificationIcon(notification.icon);
                          return (
                            <div
                              key={notification.id}
                              onClick={() => handleNotificationClick(notification)}
                              className={`p-4 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors ${
                                !notification.read ? 'bg-purple-50' : ''
                              }`}
                            >
                              <div className="flex items-start space-x-3">
                                <div className={`mt-1 ${notification.iconColor}`}>
                                  <IconComponent className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${!notification.read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {notification.timeAgo}
                                  </p>
                                </div>
                                {!notification.read && (
                                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <Bell className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                        <p className="text-sm">No notifications yet</p>
                        <p className="text-xs text-gray-400 mt-1">We'll notify you when something happens</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button 
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <img
                src={AvatarService.getAvatarUrl(user?.avatar ?? null, user?.name, user?.email)}
                alt={user?.name}
                className="w-8 h-8 rounded-full object-cover border border-gray-200"
              />
              <span className="text-sm font-medium text-gray-700 hidden sm:inline">{user?.name}</span>
            </button>

            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                >
                  <div className="p-2">
                    <button 
                      onClick={handleProfileClick}
                      className="flex items-center space-x-2 w-full p-2 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <User className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">Profile</span>
                    </button>
                    <button 
                      onClick={handleSettingsClick}
                      className="flex items-center space-x-2 w-full p-2 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <Settings className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">Settings</span>
                    </button>
                    <hr className="my-2" />
                    <button 
                      onClick={logout}
                      className="flex items-center space-x-2 w-full p-2 hover:bg-gray-50 rounded-lg transition-colors text-red-600"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm">Sign out</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Mobile Search Modal */}
      <MobileSearchModal
        isOpen={isMobileSearchOpen}
        onClose={closeMobileSearch}
      />
    </nav>
  );
}